import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginCredentials } from '../types';
import { apiService } from '../services/api';
import { useInactivityLogout } from '../hooks/useInactivityLogout';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Get inactivity timeout from environment variable (in minutes), default to 30
  const inactivityTimeoutMinutes = process.env.REACT_APP_INACTIVITY_TIMEOUT_MINUTES 
    ? parseInt(process.env.REACT_APP_INACTIVITY_TIMEOUT_MINUTES, 10) 
    : 30;

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('accessToken');

    if (storedUser && token) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Only allow admin role
        if (parsedUser.role === 'admin') {
          setUser(parsedUser);
        } else {
          // Not an admin, clear storage
          localStorage.clear();
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.clear();
      }
    }

    setLoading(false);
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await apiService.login(credentials);
      
      if (response.success && response.data.user) {
        // Check if user is admin
        if (response.data.user.role !== 'admin') {
          throw new Error('Access denied. Admin privileges required.');
        }

        setUser(response.data.user);
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    apiService.logout();
    setUser(null);
  };

  const isAdmin = user?.role === 'admin';

  // Setup automatic logout on inactivity
  useInactivityLogout({
    onLogout: logout,
    timeoutMinutes: inactivityTimeoutMinutes,
    isAuthenticated: !!user,
  });

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
